/**
 * @fileoverview Module Usage Analytics
 * @description Shows which modules/pages are most visited and where users spend time.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { appInsightsApi } from '../services/appInsightsApi';
import { formatDuration } from '../utils/timeRangeUtils';

const TIME_OPTIONS = [
  { key: 'P7D', label: '7 days' },
  { key: 'P30D', label: '30 days' },
];

// Map route paths to friendly module names
const PATH_TO_MODULE = {
  '/copilot-cost': 'FinOps',
  '/github-metrics': 'Copilot Metrics',
  '/lines-of-code': 'Lines of Code',
  '/ai-leverage': 'AI Test Generation',
  '/github-user-metrics': 'GH User Metrics',
  '/project-savings-report': 'Automation Leverage Report',
  '/model-context-protocol': 'AI Native Test Automation',
  '/regression-optimizer': 'Regression Optimizer',
  '/pipeline-analysis': 'Pipeline Analysis',
  '/pipeline-service': 'Pipeline Service',
  '/pnr-generator': 'PNR Generator',
  '/deadhead-pnr': 'DH PNR Generator',
  '/ai-test-data-generation': 'AI Agent Orchestrator',
  '/portfolio/jcte': 'JCTE Portfolio',
  '/portfolio/airport-ops': 'AirportOps Portfolio',
  '/incident-analysis': 'Incident Intelligence',
  '/reports': 'Incident Analyser',
  '/test-calendar': 'Test Calendar',
  '/admin/rbac': 'Access Management',
  '/admin/app-insights': 'App Insights',
  '/access-request': 'Access Request',
};

const getModuleName = (path) => {
  if (!path) return 'Unknown';
  return PATH_TO_MODULE[path] || path.replace(/^\//, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const ModuleUsageAnalytics = () => {
  const [timeRange, setTimeRange] = useState('P30D');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await appInsightsApi.getModuleUsage({ timespan: timeRange });
      setData(resp?.data || resp);
    } catch (err) {
      setError(err?.message || 'Failed to fetch module usage data');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  const rows = data?.rows || [];
  if (!rows.length) {
    return (
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No module usage data available for this period.</Typography>
      </Paper>
    );
  }

  // Prepare chart data (top 10 modules by view count)
  const topModules = rows.slice(0, 10);
  const chartNames = topModules.map((r) => getModuleName(r.page_path || r.name));
  const chartViews = topModules.map((r) => r.view_count || 0);
  const chartUsers = topModules.map((r) => r.unique_users || 0);

  return (
    <Box sx={{ mb: 3 }}>
      {/* Header with time toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#01426A' }}>
          Module Usage Analytics
        </Typography>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={(_, val) => val && setTimeRange(val)}
          size="small"
        >
          {TIME_OPTIONS.map((t) => (
            <ToggleButton key={t.key} value={t.key} sx={{ textTransform: 'none' }}>
              {t.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Bar chart — top modules by views */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Top Modules by Page Views
        </Typography>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          {topModules.map((row, idx) => {
            const name = chartNames[idx];
            const views = row.view_count || 0;
            const users = row.unique_users || 0;
            const maxViews = topModules[0]?.view_count || 1;
            const viewPct = (views / maxViews) * 100;
            const userPct = (users / maxViews) * 100;
            return (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ width: 200, minWidth: 200, fontSize: '0.78rem', fontWeight: 500, textAlign: 'right', color: '#333' }}
                >
                  {name}
                </Typography>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ height: 14, width: `${viewPct}%`, minWidth: 2, bgcolor: '#01426A', borderRadius: 1, transition: 'width 0.3s' }} />
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#01426A', fontWeight: 600 }}>{views}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ height: 10, width: `${userPct}%`, minWidth: 2, bgcolor: '#0097A9', borderRadius: 1, transition: 'width 0.3s' }} />
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#0097A9' }}>{users} users</Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
          <Box sx={{ display: 'flex', gap: 2, mt: 1.5, justifyContent: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#01426A', borderRadius: 1 }} />
              <Typography variant="caption">Views</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#0097A9', borderRadius: 1 }} />
              <Typography variant="caption">Unique Users</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Detailed table */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Detailed Module Usage
        </Typography>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Module / Page</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Views</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Unique Users</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Avg Time on Page</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Total Time (min)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>
                    <Chip
                      label={getModuleName(row.page_path || row.name)}
                      size="small"
                      variant="outlined"
                      sx={{ maxWidth: 250 }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {(row.view_count || 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {(row.unique_users || 0).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {row.avg_seconds_on_page != null ? `${row.avg_seconds_on_page}s` : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {row.total_minutes != null ? row.total_minutes.toLocaleString() : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ModuleUsageAnalytics;
